import { createContext, type ReactNode } from 'react';

/**
 * Result handed to a consumer's `onAuthenticated` callback after any
 * successful auth flow (email/password, 2FA, magic link, SSO). The
 * consumer decides what to do next — typically fetch the current user
 * and navigate into the app, gated on `nextApp` if it's a member-only
 * consumer (e.g. the RN mobile app). Tokens are already in storage by
 * the time this fires.
 */
export type OnAuthenticatedResult = {
  /**
   * Fresh access token from the auth flow. Not strictly needed by most
   * consumers (storage already holds it) — passed for convenience so the
   * callback doesn't have to `await getToken()`.
   */
  token: string;
  /**
   * `next_app` identifier from Apigee (`member_app`, `member_legacy_app`,
   * `member_onboarding`, `provider_app`, `care_coordinator`, `health_guide`,
   * ...). Consumers that only support a subset (RN = members only) should
   * check this and reject / sign out if it's outside their allowlist.
   */
  nextApp?: string;
};

/**
 * Optional consumer callback fired after a successful auth flow on
 * NATIVE, in place of the web-only URL redirect
 * (`window.location.href = destination`). Returning nothing is fine;
 * throwing surfaces to the caller as a login failure.
 *
 * When absent (the standalone web deploy sets no callback), login falls
 * back to `redirectAfterAuth` + `window.location.href`. Web behavior is
 * unchanged.
 */
export type OnAuthenticatedFn = (result: OnAuthenticatedResult) => void | Promise<void>;

export const OnAuthenticatedContext = createContext<OnAuthenticatedFn | null>(null);

type OnAuthenticatedProviderProps = {
  onAuthenticated?: OnAuthenticatedFn;
  children: ReactNode;
};

export const OnAuthenticatedProvider = ({
  onAuthenticated,
  children,
}: OnAuthenticatedProviderProps) => (
  <OnAuthenticatedContext.Provider value={onAuthenticated ?? null}>
    {children}
  </OnAuthenticatedContext.Provider>
);
